import React from 'react'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { Input } from '../../../components/ui/Input'
import { apiFetch, ApiError } from '../../../lib/api-client'
import { useAuth } from '../../../lib/auth/AuthContext'

type EntityType = 'property' | 'lease'
type FieldType = 'text' | 'number' | 'date' | 'boolean'

interface FieldDef {
  id: string
  company_id: string
  entity_type: EntityType
  label: string
  field_type: FieldType
  sort_order: number
}

const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Tekst',
  number: 'Tal',
  date: 'Dato',
  boolean: 'Ja/nej',
}

const headingStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8',
}

const selectStyle: React.CSSProperties = {
  display: 'block', width: '100%', height: 36, padding: '0 10px', borderRadius: 7,
  border: '1px solid #e8ecf4', fontSize: 13, color: '#0f172a', background: '#fff',
  fontFamily: 'inherit', boxSizing: 'border-box',
}

function apiMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 409) {
    // Backend sends a Danish message (duplicate label / per-type limit).
    return e.message
  }
  if (e instanceof ApiError && e.status === 403) return 'Kun administratorer kan ændre egne felter.'
  if (e instanceof ApiError && e.status === 404) return 'Feltet findes ikke længere — genindlæs siden.'
  if (e instanceof ApiError && e.status === 422) return 'Ugyldigt feltnavn eller type.'
  return fallback
}

function Section({
  companyId, entityType, title, onNotice, isAdmin,
}: {
  isAdmin: boolean
  companyId: string
  entityType: EntityType
  title: string
  onNotice: (msg: string) => void
}) {
  const base = `/api/v1/companies/${companyId}/custom-fields`
  const [defs, setDefs] = React.useState<FieldDef[] | null>(null)
  const [loadError, setLoadError] = React.useState(false)
  const [newLabel, setNewLabel] = React.useState('')
  const [newType, setNewType] = React.useState<FieldType>('text')
  const [adding, setAdding] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [editLabel, setEditLabel] = React.useState('')
  const [confirmId, setConfirmId] = React.useState<string | null>(null)
  const [busyId, setBusyId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    apiFetch<FieldDef[]>(`${base}?entity_type=${entityType}`)
      .then((list) => setDefs([...list].sort((a, b) => a.sort_order - b.sort_order)))
      .catch(() => setLoadError(true))
  }, [base, entityType])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const label = newLabel.trim()
    if (!label) {
      setError('Feltnavn er påkrævet.')
      return
    }
    setAdding(true)
    setError(null)
    try {
      const nextOrder = defs && defs.length > 0 ? Math.max(...defs.map((d) => d.sort_order)) + 1 : 0
      const created = await apiFetch<FieldDef>(base, {
        method: 'POST',
        body: JSON.stringify({ entity_type: entityType, label, field_type: newType, sort_order: nextOrder }),
      })
      setDefs((prev) => [...(prev ?? []), created])
      setNewLabel('')
      setNewType('text')
    } catch (err) {
      setError(apiMessage(err, 'Kunne ikke oprette feltet. Prøv igen.'))
    } finally {
      setAdding(false)
    }
  }

  const handleRename = async (def: FieldDef) => {
    const label = editLabel.trim()
    if (!label) {
      setError('Feltnavn er påkrævet.')
      return
    }
    if (label === def.label) {
      setEditingId(null)
      return
    }
    setBusyId(def.id)
    setError(null)
    try {
      const updated = await apiFetch<FieldDef>(`${base}/${def.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ label }),
      })
      setDefs((prev) => (prev ?? []).map((d) => (d.id === def.id ? { ...d, ...updated } : d)))
      setEditingId(null)
    } catch (err) {
      setError(apiMessage(err, 'Kunne ikke omdøbe feltet. Prøv igen.'))
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (def: FieldDef) => {
    setBusyId(def.id)
    setError(null)
    try {
      await apiFetch<unknown>(`${base}/${def.id}`, { method: 'DELETE' }).catch((err) => {
        // 204 has no body; apiFetch's json() throws a SyntaxError on success.
        if (!(err instanceof SyntaxError)) throw err
      })
      setDefs((prev) => (prev ?? []).filter((d) => d.id !== def.id))
      setConfirmId(null)
      onNotice('Feltet er slettet')
    } catch (err) {
      setError(apiMessage(err, 'Kunne ikke slette feltet. Prøv igen.'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 10, marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf4' }}>
        <span style={headingStyle}>{title}</span>
      </div>

      {loadError && (
        <div style={{ padding: 24, fontSize: 13, color: '#dc2626' }}>Kunne ikke hente felter.</div>
      )}
      {!loadError && defs === null && (
        <div style={{ padding: 24, fontSize: 13, color: '#94a3b8' }}>Henter…</div>
      )}
      {defs !== null && defs.length === 0 && (
        <div style={{ padding: 24, fontSize: 13, color: '#94a3b8' }}>Der er endnu ikke oprettet nogen egne felter.</div>
      )}

      {defs !== null && defs.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Feltnavn', 'Type', ''].map((h) => (
                <th key={h} style={{
                  padding: '8px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e8ecf4',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {defs.map((def, i) => {
              const isEditing = editingId === def.id
              const isConfirming = confirmId === def.id
              const busy = busyId === def.id
              return (
                <tr key={def.id} style={{ borderBottom: i === defs.length - 1 ? 'none' : '1px solid #e8ecf4' }}>
                  <td style={{ padding: '10px 20px', color: '#0f172a', fontWeight: 500, overflowWrap: 'anywhere' }}>
                    {isEditing ? (
                      <form
                        onSubmit={(e) => { e.preventDefault(); void handleRename(def) }}
                        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <div style={{ minWidth: 200, flex: 1 }}>
                          <Input
                            aria-label="Feltnavn"
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <Button type="submit" size="sm" isLoading={busy}>Gem</Button>
                        <Button type="button" size="sm" variant="secondary" onClick={() => { setEditingId(null); setError(null) }}>
                          Annuller
                        </Button>
                      </form>
                    ) : (
                      def.label
                    )}
                  </td>
                  <td style={{ padding: '10px 20px', whiteSpace: 'nowrap' }}>
                    <Chip variant="gray">{TYPE_LABELS[def.field_type]}</Chip>
                  </td>
                  <td style={{ padding: '10px 20px', textAlign: 'right' }}>
                    {isAdmin && !isEditing && !isConfirming && (
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <Button size="sm" variant="secondary" onClick={() => { setEditingId(def.id); setEditLabel(def.label); setConfirmId(null); setError(null) }}>
                          Omdøb
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setConfirmId(def.id); setEditingId(null); setError(null) }}>
                          Slet felt
                        </Button>
                      </div>
                    )}
                    {isConfirming && (
                      <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>Værdier på felten bliver ikke længere vist. Slet?</span>
                        <Button size="sm" variant="danger" isLoading={busy} onClick={() => void handleDelete(def)}>Slet felt</Button>
                        <Button size="sm" variant="secondary" onClick={() => setConfirmId(null)}>Annuller</Button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {isAdmin && (
      <form
        onSubmit={handleAdd}
        style={{
          display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 160px auto', gap: 12,
          alignItems: 'end', padding: '16px 20px', borderTop: '1px solid #e8ecf4',
        }}
      >
        <Input
          label="Feltnavn"
          id={`egnefelter-${entityType}-label`}
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
        />
        <div>
          <label
            htmlFor={`egnefelter-${entityType}-type`}
            style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}
          >
            Type
          </label>
          <select
            id={`egnefelter-${entityType}-type`}
            value={newType}
            onChange={(e) => setNewType(e.target.value as FieldType)}
            style={selectStyle}
          >
            {(Object.keys(TYPE_LABELS) as FieldType[]).map((t) => (
              <option key={t} value={t}>{TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <Button type="submit" isLoading={adding}>Tilføj felt</Button>
      </form>
      )}

      {error && (
        <p role="alert" style={{ fontSize: 12, color: '#dc2626', margin: 0, padding: '0 20px 14px' }}>{error}</p>
      )}
    </div>
  )
}

export function EgneFelterTab({ companyId }: { companyId: string }) {
  const { user, isLoading } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [notice, setNotice] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!notice) return
    const t = window.setTimeout(() => setNotice(null), 4000)
    return () => window.clearTimeout(t)
  }, [notice])

  return (
    <div style={{ maxWidth: 820 }}>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
        Definér egne felter, som kan udfyldes på ejendomme og lejemål. Typen kan ikke ændres, efter feltet er oprettet.
      </p>
      {!isLoading && !isAdmin && (
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
          Kun administratorer kan oprette, omdøbe eller slette egne felter.
        </p>
      )}
      {notice && (
        <p role="status" style={{ fontSize: 13, color: '#16a34a', margin: '0 0 16px' }}>{notice}</p>
      )}
      <Section companyId={companyId} entityType="property" title="Ejendom" onNotice={setNotice} isAdmin={isAdmin} />
      <Section companyId={companyId} entityType="lease" title="Lejemål" onNotice={setNotice} isAdmin={isAdmin} />
    </div>
  )
}
